/**
 * What the signed-in user may do, for hiding and disabling controls (D14).
 * The server checks every request itself; this only keeps the UI from
 * offering what it would refuse.
 *
 * The grants come from `GET /me`. A node does not serve `/me` (the tenancy
 * routes are the cloud's), so there the grants are **unknown** — `null` — and
 * every control is offered: the node's answer (a 403, a nack) is then what
 * the user sees. Unknown is never read as "nothing allowed": an operator on a
 * node must still be able to take.
 */
import type { PermissionIdWire } from '@norna/api-contract/permissions.js';
import { isPermitted, isPermittedAnywhere, type PermissionGrant, type ResourceScope } from '@norna/domain/access.js';

/** The caller's grants, or null when this backend cannot say (a node). */
export type Grants = readonly PermissionGrant[] | null;

/** The grants a `/me` answer carries (`available: false` → unknown). */
export function grantsOf(me: { readonly available: true; readonly me: { readonly grants: readonly PermissionGrant[] } } | { readonly available: false } | undefined): Grants {
  if (me === undefined || !me.available) return null;
  return me.me.grants;
}

/** Whether to offer an action needing `permission` on `resource`. Unknown grants offer it. */
export function allows(grants: Grants, permission: PermissionIdWire, resource: ResourceScope): boolean {
  return grants === null || isPermitted(grants, permission, resource);
}

/** Whether `permission` is held on SOME resource (a create whose resource is picked in the dialog). Unknown grants offer it. */
export function allowsSomewhere(grants: Grants, permission: PermissionIdWire): boolean {
  return grants === null || isPermittedAnywhere(grants, permission);
}
